// Side bay (flight plans + active traffic), wind briefing, alert rail, and the
// header stats.

import {
  DELAY_STEP, HUBS, TIME_SLOTS, TIME_SLOT_LABELS, COMPASS, TYPES, MAX_WIND_INPUT, MAX_INCIDENTS, PREDICT_URGENT,
} from '../config/constants.js';
import { timeOfDay, typesGroundedBy, compassLabel } from '../core/wind.js';
import { liveConflictPairs, rerouteHub } from '../core/sim.js';
import { scoreOf, formatScore } from '../core/score.js';
import { $, setText, setClass, setHidden, setHTML, syncList } from './dom.js';

const isUrgent = (p, elapsed) => p.predicted && !p.grounded && p.departAt - elapsed < PREDICT_URGENT;

/**
 * Scroll a list just enough to show one of its items. Only the list scrolls,
 * never the page (scrollIntoView would yank a phone-width page down to the
 * side bay whenever something is selected on the map).
 */
export function revealInList(listEl, itemEl) {
  const list = listEl.getBoundingClientRect();
  const item = itemEl.getBoundingClientRect();
  if (item.top < list.top) listEl.scrollTop += item.top - list.top - 6;
  else if (item.bottom > list.bottom) listEl.scrollTop += item.bottom - list.bottom + 6;
}

export function createPlanList(listEl, countEl, { onSelect, onDelay, onReroute }) {
  const cache = new Map();
  let shownSelection = null;

  listEl.addEventListener('click', (e) => {
    const item = e.target.closest('[data-id]');
    if (!item) return;
    const id = Number(item.dataset.id);
    const btn = e.target.closest('button[data-action]');
    if (!btn) onSelect(id);
    else if (btn.dataset.action === 'delay') onDelay(id);
    else onReroute(id);
  });

  function create(p) {
    const el = document.createElement('div');
    el.dataset.id = p.id;
    el.tabIndex = 0;
    el.innerHTML = `
      <div class="plan-row1">
        <span class="plan-call"><span class="call"></span><small></small></span>
        <span class="plan-eta"></span>
      </div>
      <div class="plan-route"><b class="from"></b> &rarr; <b class="to"></b></div>
      <div class="plan-note ground" hidden></div>
      <div class="plan-note conflict" hidden></div>
      <div class="plan-actions">
        <button type="button" class="delay" data-action="delay">Delay +${DELAY_STEP}s</button>
        <button type="button" class="reroute" data-action="reroute">Reroute</button>
      </div>`;
    return el;
  }

  function update(el, p, elapsed, selectedId) {
    const state = p.grounded ? ' grounded' : (p.predicted ? ' predicted' : '');
    setClass(el, `plan-card${state}${isUrgent(p, elapsed) ? ' urgent' : ''}${p.id === selectedId ? ' selected' : ''}`);
    el.style.setProperty('--type-color', p.color);
    setText($('.call', el), p.call);
    setText($('small', el), `max ${p.maxWind}mph · ${p.power === 'ev' ? 'EV' : 'AVGAS'}`);
    setText($('.from', el), p.originLabel);
    setText($('.to', el), p.destLabel);
    const eta = $('.plan-eta', el);
    if (p.hubHeld) { setClass(eta, 'plan-eta wind'); setText(eta, 'PAD HOLD'); }
    else if (p.grounded && p.windHeld) { setClass(eta, 'plan-eta wind'); setText(eta, 'WIND HOLD'); }
    else {
      setClass(eta, `plan-eta${isUrgent(p, elapsed) ? ' due' : ''}`);
      setText(eta, `T-${Math.max(0, Math.ceil(p.departAt - elapsed))}s`);
    }
    const ground = $('.plan-note.ground', el);
    setHidden(ground, !p.grounded && !p.hubHeld);
    if (p.hubHeld) setText(ground, `Waiting: the pad at ${HUBS[p.hubIdx].name} is blocked`);
    else if (p.grounded) setText(ground, `Grounded: ${p.windAtDepart}mph forecast is over the ${p.type} limit`);
    const conflict = $('.plan-note.conflict', el);
    setHidden(conflict, !p.predicted);
    if (p.predicted) setText(conflict, `⚠ Launches into conflict: ${p.conflictWith.join(', ')}`);
    setText($('button.reroute', el), `Reroute via ${rerouteHub(p).name}`);
  }

  return {
    /** `selectedId` is the selected plan's id, or null. */
    update(s, selectedId) {
      setText(countEl, `${s.plans.length} filed`);
      const sorted = [...s.plans].sort((a, b) => a.departAt - b.departAt || a.id - b.id);
      syncList(listEl, cache, sorted, create, (el, p) => update(el, p, s.elapsed, selectedId));
      if (selectedId !== shownSelection && cache.has(selectedId)) revealInList(listEl, cache.get(selectedId));
      shownSelection = selectedId;
    },
  };
}

export function createTrafficList(listEl, countEl, { onSelect, onHold, onClear }) {
  const cache = new Map();
  let shownSelection = null;

  listEl.addEventListener('click', (e) => {
    const item = e.target.closest('[data-id]');
    if (!item) return;
    const id = Number(item.dataset.id);
    const btn = e.target.closest('button[data-action]');
    if (!btn) onSelect(id);
    else if (btn.dataset.action === 'hold') onHold(id);
    else onClear(id);
  });

  function create(d) {
    const el = document.createElement('div');
    el.dataset.id = d.id;
    el.tabIndex = 0;
    el.innerHTML = `
      <div class="strip-row1">
        <span class="strip-call"></span>
        <span class="strip-status"></span>
      </div>
      <div class="strip-route"><b class="from"></b> &rarr; <b class="to"></b></div>
      <div class="strip-actions">
        <button type="button" class="hold" data-action="hold">Hold</button>
        <button type="button" class="clear" data-action="clear">Clear</button>
      </div>`;
    return el;
  }

  function update(el, d, selectedId) {
    setClass(el, `strip${d.id === selectedId ? ' selected' : ''}${d.state === 'conflict' ? ' conflict' : ''}`);
    el.style.setProperty('--type-color', d.color);
    setText($('.strip-call', el), d.call);
    const status = $('.strip-status', el);
    let label = { holding: 'Holding', conflict: 'Conflict', enroute: 'En route', landed: 'On pad' }[d.state];
    if (d.padWait && d.state === 'holding') label = 'Pattern';
    if (d.emergency) label = `Emergency ${Math.ceil(d.emergency.left)}s`;
    setClass(status, `strip-status ${d.emergency ? 'conflict' : d.state}`);
    setText(status, label);
    setText($('.from', el), d.originLabel);
    setText($('.to', el), d.destLabel);
    const hold = $('.hold', el);
    const clear = $('.clear', el);
    const grounded = d.state === 'holding' || d.state === 'landed';
    if (hold.disabled !== grounded) hold.disabled = grounded;
    if (clear.disabled !== (d.state !== 'holding')) clear.disabled = d.state !== 'holding';
  }

  return {
    /** `selectedId` is the selected drone's id, or null. */
    update(s, selectedId) {
      setText(countEl, `${s.drones.length} airborne`);
      const sorted = [...s.drones].sort((a, b) => (a.call < b.call ? -1 : 1));
      syncList(listEl, cache, sorted, create, (el, d) => update(el, d, selectedId));
      if (selectedId !== shownSelection && cache.has(selectedId)) revealInList(listEl, cache.get(selectedId));
      shownSelection = selectedId;
    },
  };
}

export function createWindBriefing({ slotsEl, nowEl, limitsEl }, s, { onChange }) {
  limitsEl.innerHTML = `Max wind: ${[...TYPES].sort((a, b) => a.maxWind - b.maxWind)
    .map((t) => `<b>${t.prefix}</b> ${t.maxWind}`).join(' · ')} mph`;

  const slots = {};
  for (const slot of TIME_SLOTS) {
    const label = TIME_SLOT_LABELS[slot];
    const el = document.createElement('div');
    el.className = 'wind-slot';
    el.innerHTML = `
      <span class="slot-label">${label}</span>
      <div class="wind-slot-row">
        <input type="number" min="0" max="${MAX_WIND_INPUT}" step="1" aria-label="${label} wind speed (mph)">
        <span class="unit">mph from</span>
        <select aria-label="${label} wind direction">
          ${COMPASS.map((c) => `<option value="${c.deg}">${c.label}</option>`).join('')}
        </select>
      </div>
      <span class="grounds"></span>`;
    const speed = $('input', el);
    const dir = $('select', el);
    speed.value = s.wind[slot].speed;
    dir.value = String(s.wind[slot].dir);
    speed.addEventListener('input', () => onChange(slot, { speed: parseFloat(speed.value) }));
    dir.addEventListener('change', () => onChange(slot, { dir: parseInt(dir.value, 10) }));
    slots[slot] = { el, grounds: $('.grounds', el), speed, dir };
    slotsEl.appendChild(el);
  }

  return {
    /** Show a shift's wind in the inputs (a new shift may bring its own). */
    load(state) {
      for (const slot of TIME_SLOTS) {
        slots[slot].speed.value = state.wind[slot].speed;
        slots[slot].dir.value = String(state.wind[slot].dir);
      }
    },
    /** Lesson 1 flies a fixed wind: the inputs show it but can't change it. */
    setLocked(locked) {
      for (const slot of TIME_SLOTS) {
        slots[slot].speed.disabled = locked;
        slots[slot].dir.disabled = locked;
      }
      slotsEl.classList.toggle('locked', locked);
    },
    update(state) {
      const now = timeOfDay(state.elapsed);
      for (const slot of TIME_SLOTS) {
        const { el, grounds } = slots[slot];
        el.classList.toggle('active', slot === now);
        const g = typesGroundedBy(state.wind[slot].speed, TYPES);
        setText(grounds, g.length ? `Grounds ${g.join(', ')}` : 'All types fly');
        grounds.classList.toggle('on', g.length > 0);
      }
      const w = state.wind[now];
      setText(nowEl, `Now: ${TIME_SLOT_LABELS[now]} — ${w.speed}mph from ${compassLabel(w.dir)}`);
    },
  };
}

export function updateAlertRail(railEl, s) {
  const pairs = liveConflictPairs(s).slice(0, 3);
  const predicted = s.plans.filter((p) => p.predicted && !p.grounded);
  const urgent = predicted.filter((p) => isUrgent(p, s.elapsed)).length;
  const grounded = s.plans.filter((p) => p.grounded).length;
  let html = pairs.map(([a, b]) => `<div class="conflict-pill">⚠ ${a} ↔ ${b}</div>`).join('');
  // your own aircraft: conflicts coming up in the next few seconds
  for (const p of (s.ops?.predicted ?? []).slice(0, 2)) {
    html += `<div class="predict-pill urgent">☉ ${p.a.call} ↔ ${p.b.call} in ${Math.ceil(p.in)}s</div>`;
  }
  if (predicted.length) {
    const n = predicted.length;
    html += `<div class="predict-pill${urgent ? ' urgent' : ''}">☉ ${n} ${n > 1 ? 'plans launch' : 'plan launches'} into a conflict</div>`;
  }
  if (grounded) html += `<div class="ground-pill">${grounded} ${grounded > 1 ? 'plans' : 'plan'} grounded by wind</div>`;
  const held = s.plans.filter((p) => p.hubHeld).length;
  if (held) html += `<div class="ground-pill">${held} ${held > 1 ? 'plans wait' : 'plan waits'} for a blocked pad</div>`;
  setHTML(railEl, html);
}

export function updateStats(els, s, best) {
  setText(els.day, s.day);
  setText(els.active, s.drones.length);
  setText(els.cleared, s.cleared);
  setText(els.adjusted, s.adjusted + s.holds);
  setText(els.grounded, s.groundedTotal);
  setText(els.incidents, `${s.incidents} / ${MAX_INCIDENTS}`);
  setText(els.score, formatScore(scoreOf(s)));
  setText(els.best, best === null ? 'Best —' : `Best ${formatScore(best)}`);
}
